import {
  doc,
  updateDoc,
  arrayUnion,
  getDocs,
  collection,
  query,
  where,
  addDoc,
  serverTimestamp,
  runTransaction,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase-config";
import { computeTicketSplit } from "../lib/eventMoney";
import { getStripeMode } from "../lib/stripeMode";

// ─── Conversation helpers ──────────────────────────────────────────────────

/**
 * Looks up the conversation tied to an event by its `hangout` ref.
 * Returns the conversation document id, or null when the event doesn't
 * have a conversation yet (older events created before chat was wired
 * up still exist in the wild).
 *
 * Kept as its own helper because Firestore queries can't run inside a
 * transaction — callers that need to update the conversation as part
 * of a transactional purchase do this lookup BEFORE opening the txn
 * and pass the id in.
 */
export const findEventConversationId = async (eventId) => {
  const eventRef = doc(db, "events", eventId);
  const convoQuery = query(
    collection(db, "conversations"),
    where("hangout", "==", eventRef)
  );
  const convoSnap = await getDocs(convoQuery);
  if (convoSnap.empty) return null;
  return convoSnap.docs[0].id;
};

/**
 * Adds a user to a conversation's members and initializes their readStatus.
 * @param {string} userId - UID of the user to add.
 * @param {string} conversationId - ID of the conversation document.
 */
export const addUserToConversation = async (userId, conversationId) => {
  try {
    const userRef = doc(db, "users", userId);
    const convoRef = doc(db, "conversations", conversationId);

    await updateDoc(convoRef, {
      members: arrayUnion(userRef),
      [`readStatus.${userId}`]: 0,
      lastUpdated: serverTimestamp(),
    });

    console.log(`✅ Added user ${userId} to conversation ${conversationId}`);
  } catch (error) {
    console.error("❌ Error adding user to conversation:", error);
    throw error;
  }
};

// ─── Membership / ticket helpers ───────────────────────────────────────────

/**
 * Adds a user to the event guest list and to the corresponding conversation.
 * This is the FREE / non-paid path — paid joins go through
 * `recordTicketPurchase` so we capture the money split in the same
 * transaction. Kept around for free events and the legacy join flow.
 *
 * @param {string} eventId - ID of the event document.
 * @param {string} userId - UID of the user.
 */
export const addUserToGuestList = async (eventId, userId) => {
  try {
    const eventRef = doc(db, "events", eventId);
    const userRef = doc(db, "users", userId);

    await updateDoc(eventRef, {
      members: arrayUnion(userRef),
    });

    const ticketsColRef = collection(db, "events", eventId, "tickets");
    await addDoc(ticketsColRef, {
      event_id: eventId,
      user: userId,
      paidAmount: 0,
      createdAt: serverTimestamp(),
    });

    const conversationId = await findEventConversationId(eventId);
    if (conversationId) {
      await addUserToConversation(userId, conversationId);
    } else {
      console.warn(`⚠️ No conversation found for event ID: [#${eventId}]`);
    }

    console.log(
      `✅ Added user ${userId} to event ${eventId} and its conversation`
    );
  } catch (error) {
    console.error("❌ Error adding user to guest list or conversation:", error);
    throw error;
  }
};

/**
 * Records a paid ticket purchase: runs the waterfall split, writes the
 * top-level `transactions` doc, increments the event's revenue
 * counters, adds the buyer to the event's `members`, and creates the
 * per-event ticket subdoc — all in a single Firestore transaction so
 * the totals never drift from the underlying purchase records.
 *
 * Conversation membership is added AFTER the transaction commits
 * because Firestore queries can't run inside a transaction (we'd
 * otherwise need to know the conversation id ahead of time). A failure
 * to add to the conversation is logged but does NOT roll back the
 * purchase — the user has already paid and should not lose their seat
 * because chat plumbing hiccupped.
 *
 * Pricing convention:
 *   `paidAmount` is in CENTS and represents the BASE ticket price the
 *   buyer paid (taxes / fees handled separately by Stripe). The
 *   waterfall is computed off this amount.
 *
 * @param {Object}   args
 * @param {string}   args.eventId           - Event document id.
 * @param {string}   args.buyerUid          - UID of the buyer.
 * @param {number}   args.paidAmount        - Ticket base price in cents.
 * @param {string}  [args.paymentIntentId]  - Stripe PaymentIntent id.
 * @param {string}  [args.stripeMode]       - "test" | "live"; defaults to current.
 * @param {string}  [args.currency]         - ISO currency code; defaults to "usd".
 * @param {Object}  [args.serverSplit]      - Pre-computed split from the
 *   server (`authorizeAndCapturePayment`). When provided, the ledger
 *   records exactly what Stripe distributed instead of recomputing
 *   from `revenueCollected` (which can drift from the server's
 *   snapshot under concurrent purchases).
 * @param {number}  [args.serverSplit.venueShare]
 * @param {number}  [args.serverSplit.producerShare]
 * @param {number}  [args.serverSplit.platformShare]
 *
 * @returns {Promise<{transactionId: string, split: {venueShare:number, producerShare:number, platformShare:number}}>}
 */
export const recordTicketPurchase = async ({
  eventId,
  buyerUid,
  paidAmount,
  paymentIntentId = null,
  stripeMode,
  currency = "usd",
  serverSplit = null,
}) => {
  if (!eventId) throw new Error("recordTicketPurchase: eventId is required");
  if (!buyerUid) throw new Error("recordTicketPurchase: buyerUid is required");

  const amount = Math.max(0, Math.round(Number(paidAmount) || 0));

  console.log("[recordTicketPurchase] start", {
    eventId,
    buyerUid,
    paidAmountCents: amount,
    paymentIntentId,
    stripeMode: stripeMode || getStripeMode(),
    currency,
  });

  // Free joins shouldn't go through the purchase pipeline — there's
  // nothing to split and we want them to land in the same code path
  // as RSVPs.
  if (amount === 0) {
    console.log(
      "[recordTicketPurchase] free join — routing to addUserToGuestList"
    );
    await addUserToGuestList(eventId, buyerUid);
    return {
      transactionId: null,
      split: { venueShare: 0, producerShare: 0, platformShare: 0 },
    };
  }

  const eventRef = doc(db, "events", eventId);
  const userRef = doc(db, "users", buyerUid);

  // Look up the conversation up-front so we can add the buyer to it
  // after the txn commits. Doing this inside the transaction would
  // throw because Firestore disallows queries in transactions.
  const conversationId = await findEventConversationId(eventId);

  // Pre-allocate ids for the new docs we'll write inside the
  // transaction. Using `doc(collection(...))` gives us client-side ids
  // we can reference from `transaction.set` (you can't `addDoc` in a
  // transaction).
  const txRef = doc(collection(db, "transactions"));
  const ticketRef = doc(collection(db, "events", eventId, "tickets"));

  const result = await runTransaction(db, async (transaction) => {
    const eventSnap = await transaction.get(eventRef);
    if (!eventSnap.exists()) {
      throw new Error(`recordTicketPurchase: event ${eventId} not found`);
    }
    const eventData = eventSnap.data();

    // venueMinimumRevenue is stored in dollars; the split runs on
    // cents so everything reconciles to integer math.
    const venueMinimum = Math.round(
      Math.max(0, Number(eventData.venueMinimumRevenue) || 0) * 100
    );
    const runningRevenueBefore = Math.max(
      0,
      Math.round(Number(eventData.revenueCollected) || 0)
    );

    // Prefer the server's split if it was provided — that's what Stripe
    // actually moved. Recomputing here would diverge under concurrent
    // purchases (the server's snapshot of `revenueCollected` can be a
    // few cents stale relative to this transaction).
    let split;
    if (
      serverSplit &&
      Number.isFinite(serverSplit.venueShare) &&
      Number.isFinite(serverSplit.producerShare) &&
      Number.isFinite(serverSplit.platformShare) &&
      serverSplit.venueShare +
        serverSplit.producerShare +
        serverSplit.platformShare ===
        amount
    ) {
      split = {
        venueShare: serverSplit.venueShare,
        producerShare: serverSplit.producerShare,
        platformShare: serverSplit.platformShare,
      };
    } else {
      split = computeTicketSplit({
        ticketPrice: amount,
        runningRevenue: runningRevenueBefore,
        venueMinimum,
      });
    }

    const runningRevenueAfter = runningRevenueBefore + amount;

    // Pretty-print the waterfall decision so you can confirm in the
    // console that the math matches expectations. All amounts in cents.
    const fmt = (cents) => `$${(cents / 100).toFixed(2)} (${cents}¢)`;
    const venuePct = amount > 0 ? Math.round((split.venueShare / amount) * 100) : 0;
    const producerPct = amount > 0 ? Math.round((split.producerShare / amount) * 100) : 0;
    const platformPct = amount > 0 ? Math.round((split.platformShare / amount) * 100) : 0;
    const phase =
      split.venueShare === amount
        ? "below-minimum (100% to venue)"
        : split.venueShare === 0
        ? "above-minimum (30/70 producer/platform)"
        : "straddling-threshold (mixed)";
    console.log("[recordTicketPurchase] waterfall split", {
      phase,
      eventId,
      eventName: eventData.name,
      hasPartnerVenue: !!eventData.partnerVenueId,
      venueOwnerUid: eventData.venueOwnerUid || null,
      ticketPrice: fmt(amount),
      venueMinimum: fmt(venueMinimum),
      runningRevenueBefore: fmt(runningRevenueBefore),
      runningRevenueAfter: fmt(runningRevenueAfter),
      venueShare: `${fmt(split.venueShare)} (${venuePct}%)`,
      producerShare: `${fmt(split.producerShare)} (${producerPct}%)`,
      platformShare: `${fmt(split.platformShare)} (${platformPct}%)`,
      reconciles:
        split.venueShare + split.producerShare + split.platformShare === amount,
    });

    const venueRevenueBefore = Math.max(
      0,
      Math.round(Number(eventData.venueRevenueCollected) || 0)
    );
    const producerRevenueBefore = Math.max(
      0,
      Math.round(Number(eventData.producerRevenueCollected) || 0)
    );
    const platformRevenueBefore = Math.max(
      0,
      Math.round(Number(eventData.platformRevenueCollected) || 0)
    );

    const transactionDoc = {
      eventId,
      eventDocId: eventId,
      eventName: eventData.name || null,
      producerUid: eventData.host || null,
      venueOwnerUid: eventData.venueOwnerUid || null,
      buyerUid,
      paidAmount: amount,
      venueShare: split.venueShare,
      producerShare: split.producerShare,
      platformShare: split.platformShare,
      runningRevenueBefore,
      runningRevenueAfter,
      paymentIntentId,
      stripeMode: stripeMode || getStripeMode(),
      currency,
      createdAt: serverTimestamp(),
    };

    transaction.set(txRef, transactionDoc);

    transaction.update(eventRef, {
      members: arrayUnion(userRef),
      revenueCollected: runningRevenueAfter,
      venueRevenueCollected: venueRevenueBefore + split.venueShare,
      producerRevenueCollected: producerRevenueBefore + split.producerShare,
      platformRevenueCollected: platformRevenueBefore + split.platformShare,
    });

    transaction.set(ticketRef, {
      event_id: eventId,
      user: buyerUid,
      paidAmount: amount,
      transactionId: txRef.id,
      venueShare: split.venueShare,
      producerShare: split.producerShare,
      platformShare: split.platformShare,
      paymentIntentId,
      stripeMode: stripeMode || getStripeMode(),
      currency,
      // serverTimestamp() isn't allowed inside transactions; use a
      // client-side Timestamp.now() so the field is still set
      // atomically with the rest of the writes.
      createdAt: Timestamp.now(),
    });

    return { split };
  });

  console.log("[recordTicketPurchase] committed", {
    transactionId: txRef.id,
    ticketId: ticketRef.id,
    split: result.split,
  });

  // Conversation add happens last and is best-effort — we don't want
  // a chat failure to revert a successful purchase. The user has
  // already been added to `members` and has a ticket on file.
  if (conversationId) {
    try {
      await addUserToConversation(buyerUid, conversationId);
    } catch (error) {
      console.error(
        `⚠️ Purchase ${txRef.id} succeeded but adding user to conversation ${conversationId} failed:`,
        error
      );
    }
  } else {
    console.warn(`⚠️ No conversation found for event ID: [#${eventId}]`);
  }

  return { transactionId: txRef.id, split: result.split };
};
